import { useSyncExternalStore } from 'react'

/** `system` follows the OS; the other two pin it. Persisted per device, not per account. */
export type ThemePreference = 'system' | 'light' | 'dark'
export type Theme = 'light' | 'dark'

export const THEME_PREFERENCES: readonly ThemePreference[] = ['system', 'light', 'dark']
export const THEME_STORAGE_KEY = 'romi.theme'
const THEME_COLOR: Record<Theme, string> = { light: '#f6f6f4', dark: '#141413' }

export function isThemePreference(value: unknown): value is ThemePreference {
  return typeof value === 'string' && (THEME_PREFERENCES as string[]).includes(value)
}

function readStored(): ThemePreference {
  try {
    const v = localStorage.getItem(THEME_STORAGE_KEY)
    return isThemePreference(v) ? v : 'system'
  } catch {
    return 'system'
  }
}

function writeStored(pref: ThemePreference) {
  try {
    if (pref === 'system') localStorage.removeItem(THEME_STORAGE_KEY)
    else localStorage.setItem(THEME_STORAGE_KEY, pref)
  } catch {
    // Private mode / blocked storage: the choice just does not survive a reload.
  }
}

function systemQuery(): MediaQueryList | undefined {
  return typeof matchMedia === 'function' ? matchMedia('(prefers-color-scheme: dark)') : undefined
}

export function systemTheme(): Theme {
  return systemQuery()?.matches ? 'dark' : 'light'
}

let preference: ThemePreference = readStored()
let resolved: Theme = resolve(preference)
const listeners = new Set<() => void>()

function resolve(pref: ThemePreference): Theme {
  return pref === 'system' ? systemTheme() : pref
}

/** `<html data-theme>` is what the CSS keys on; `theme-color` matches the browser chrome. */
function apply() {
  if (typeof document === 'undefined') return
  document.documentElement.dataset['theme'] = resolved
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', THEME_COLOR[resolved])
}

function update() {
  const next = resolve(preference)
  if (next === resolved) return
  resolved = next
  apply()
  for (const l of listeners) l()
}

export function getThemePreference(): ThemePreference {
  return preference
}

export function getTheme(): Theme {
  return resolved
}

export function subscribeTheme(listener: () => void) {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function useThemePreference(): ThemePreference {
  return useSyncExternalStore(subscribeTheme, getThemePreference)
}

/** The theme actually on screen (system resolved). */
export function useTheme(): Theme {
  return useSyncExternalStore(subscribeTheme, getTheme)
}

export function setThemePreference(pref: ThemePreference) {
  if (pref === preference) return
  preference = pref
  writeStored(pref)
  resolved = resolve(pref)
  apply()
  for (const l of listeners) l()
}

function listenToSystem() {
  systemQuery()?.addEventListener('change', update)
}

listenToSystem()
apply()

/** Test-only: back to the first-visit state (re-attaches to the stubbed `matchMedia`). */
export function resetThemeForTests() {
  listenToSystem()
  preference = readStored()
  resolved = resolve(preference)
  apply()
  for (const l of listeners) l()
}
